import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import '../../styles/inputs/searchSelectInput.scss';
import CustomSVGs from '../customSVGs/CustomSVGs';
import { arrowHeadLeftSVG, searchSmallSVG } from '../../lib/allCustomSVGs';
import { createPortal } from 'react-dom';

export interface SearchSelectOption {
  OptionValue: string | number | null | undefined;
  OptionName: string | number | null | undefined;
  OptionDisabled?: boolean;
}

interface Props {
  defaultValue: string | number | null | undefined;
  setSelect: (input: string) => void;
  selectOptionArr: SearchSelectOption[] | null | undefined;
  placeholder?: string;
  id: string;
  disabled?: boolean;
  required?: boolean;
  refreshButton?: {
    onClick: () => void;
    isLoading: boolean;
    icon: React.ReactNode;
  };
  noResultsText?: string;
  onMenuClose?: () => void;
}

type MenuPos = {
  top: number;
  left: number;
  width: number;
  placement: 'bottom' | 'top';
};

const GAP = 4;

/** ---------- SAFETY HELPERS (never throw) ---------- */
const toStr = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  try {
    // attempt best-effort stringify without crashing
    const s = (v as any).toString?.();
    return typeof s === 'string' ? s : '';
  } catch {
    return '';
  }
};

const toLower = (v: unknown): string => toStr(v).toLowerCase();

const isOption = (o: unknown): o is SearchSelectOption =>
  !!o &&
  typeof o === 'object' &&
  ('OptionName' in (o as any) || 'OptionValue' in (o as any));

/** Gracefully coerce any input into a safe array of options */
const sanitizeOptions = (
  arr: Props['selectOptionArr']
): SearchSelectOption[] => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(isOption).map((o) => ({
    OptionName: (o as SearchSelectOption).OptionName ?? '',
    OptionValue: (o as SearchSelectOption).OptionValue ?? '',
    OptionDisabled: !!(o as SearchSelectOption).OptionDisabled,
  }));
};

const SearchSelectInput: React.FC<Props> = ({
  defaultValue,
  setSelect,
  selectOptionArr,
  placeholder,
  id,
  disabled,
  required,
  refreshButton,
  noResultsText = 'No results',
  onMenuClose,
}) => {
  const safeOptions = useMemo(
    () => sanitizeOptions(selectOptionArr),
    [selectOptionArr]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(
    toStr(defaultValue)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

  const outerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  /** Keep internal state in sync, but never crash if parent sends weird data */
  useEffect(() => {
    const next = toStr(defaultValue);
    setSelectedValue(next);
    try {
      setSelect(next);
    } catch {
      // swallow – parent callback should never break this component
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const selectedOption = useMemo(
    () => safeOptions.find((o) => toStr(o.OptionValue) === selectedValue),
    [selectedValue, safeOptions]
  );

  const filteredOptions = useMemo(() => {
    const q = toLower(searchTerm.trim());
    if (!q) return safeOptions;
    try {
      return safeOptions.filter(
        (opt) =>
          toLower(opt.OptionName).includes(q) ||
          toLower(opt.OptionValue).includes(q)
      );
    } catch {
      // absolute fallback – return all rather than breaking
      return safeOptions;
    }
  }, [searchTerm, safeOptions]);

  /** Positioning helpers */
  const computeBasePosition = (): MenuPos | null => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = rect.width;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const heightGuess = 240;
    const placement: 'bottom' | 'top' =
      spaceBelow >= heightGuess || spaceBelow >= spaceAbove ? 'bottom' : 'top';

    const top =
      placement === 'bottom'
        ? rect.bottom + window.scrollY + GAP
        : rect.top + window.scrollY - heightGuess - GAP;

    const left = rect.left + window.scrollX;
    return { top, left, width, placement };
  };

  const refineWithMeasuredHeight = () => {
    if (!menuRef.current || !triggerRef.current) return;
    const tr = triggerRef.current.getBoundingClientRect();
    const mr = menuRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - tr.bottom;
    const spaceAbove = tr.top;

    const placement: 'bottom' | 'top' =
      spaceBelow >= mr.height || spaceBelow >= spaceAbove ? 'bottom' : 'top';

    const top =
      placement === 'bottom'
        ? tr.bottom + window.scrollY + GAP
        : tr.top + window.scrollY - mr.height - GAP;

    const left = tr.left + window.scrollX;

    setMenuPos({
      top: Math.max(8, top),
      left: Math.max(8, left),
      width: tr.width,
      placement,
    });
  };

  const openMenu = () => {
    if (disabled) return;
    setIsOpen(true);
    setMenuPos(computeBasePosition());
  };
  const closeMenu = () => {
    setIsOpen(false);
    setSearchTerm('');
    setHighlightIndex(-1);
    onMenuClose?.();
  };
  const toggleMenu = () => (isOpen ? closeMenu() : openMenu());

  useLayoutEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      refineWithMeasuredHeight();
      searchInputRef.current?.focus();
    });
  }, [isOpen, filteredOptions.length]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => {
      setMenuPos(computeBasePosition());
      requestAnimationFrame(refineWithMeasuredHeight);
    };
    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const inTrigger = !!triggerRef.current && triggerRef.current.contains(t);
      const inMenu = !!menuRef.current && menuRef.current.contains(t);
      if (!inTrigger && !inMenu) closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  const onControlKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (
      !isOpen &&
      (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')
    ) {
      e.preventDefault();
      openMenu();
      return;
    }
    if (isOpen && e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
    }
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    const max = Math.max(0, filteredOptions.length - 1);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((h) => Math.min(max, h + 1));
      scrollHighlightedIntoView(Math.min(max, highlightIndex + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((h) => Math.max(0, h - 1));
      scrollHighlightedIntoView(Math.max(0, highlightIndex - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex <= max) {
        const opt = filteredOptions[highlightIndex];
        if (!opt?.OptionDisabled) handleSelect(opt);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
    }
  };

  const scrollHighlightedIntoView = (index: number) => {
    const container = listRef.current;
    if (!container) return;
    const row = container.querySelector<HTMLDivElement>(
      `[data-option-index='${index}']`
    );
    if (!row) return;
    const cTop = container.scrollTop;
    const cBottom = cTop + container.clientHeight;
    const rTop = row.offsetTop;
    const rBottom = rTop + row.clientHeight;
    if (rTop < cTop) container.scrollTop = rTop;
    else if (rBottom > cBottom)
      container.scrollTop = rBottom - container.clientHeight;
  };

  const handleSelect = (opt: SearchSelectOption) => {
    const v = toStr(opt?.OptionValue);
    setSelectedValue(v);
    try {
      setSelect(v);
    } catch {
      // swallow to be fail-safe
    }
    closeMenu();
  };

  const renderValue = toStr(selectedOption?.OptionName) || selectedValue || '';

  return (
    <div className='searchSelectInput-outer' ref={outerRef}>
      <div className='searchSelectInput-label-container'>
        <label htmlFor={id}>{placeholder}</label>
        {refreshButton && (
          <button
            type='button'
            onClick={() => {
              try {
                refreshButton.onClick();
              } catch {
                /* no-op */
              }
            }}
            disabled={!!refreshButton.isLoading || !!disabled}
            className='searchSelectInput-refresh-button'
            title='Refresh'
          >
            {refreshButton.isLoading ? (
              <div className='searchSelectInput-refresh-icon searchSelectInput-refresh-icon--loading'>
                {refreshButton.icon}
              </div>
            ) : (
              <>
                <span className='searchSelectInput-refresh-text'>Refresh</span>
                <div className='searchSelectInput-refresh-icon'>
                  {refreshButton.icon}
                </div>
              </>
            )}
          </button>
        )}
      </div>

      <div className={`searchSelectInput ${disabled ? 'is-disabled' : ''}`}>
        <div
          id={id}
          tabIndex={disabled ? -1 : 0}
          className='searchSelectInput__control'
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          onClick={toggleMenu}
          onKeyDown={onControlKeyDown}
          ref={triggerRef}
          aria-required={!!required}
        >
          <div className='searchSelectInput__value' title={renderValue}>
            {renderValue || '—'}
          </div>
          <div
            data-open={isOpen}
            className='searchSelectInput__icon'
            aria-hidden='true'
          >
            <CustomSVGs svg={arrowHeadLeftSVG} />
          </div>
        </div>
      </div>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className='searchSelectInput__window'
            role='listbox'
            style={{
              position: 'absolute',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              zIndex: 10000,
            }}
          >
            <div className='searchSelectInput__search'>
              <div className='searchSelectInput__search-icon'>
                <CustomSVGs svg={searchSmallSVG} />
              </div>
              <input
                ref={searchInputRef}
                type='text'
                placeholder='Search…'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target?.value ?? '');
                  setHighlightIndex(0);
                }}
                onKeyDown={onSearchKeyDown}
              />
            </div>

            <div className='searchSelectInput__options' ref={listRef}>
              {filteredOptions.length === 0 ? (
                <div className='searchSelectInput__no-results'>
                  {noResultsText}
                </div>
              ) : (
                filteredOptions.map((opt, idx) => {
                  const key = `${toStr(opt.OptionValue)}-${idx}`;
                  const isActive = idx === highlightIndex;
                  const disabledOpt = !!opt.OptionDisabled;
                  return (
                    <div
                      key={key}
                      className={`searchSelectInput__option${
                        isActive ? ' is-active' : ''
                      }${disabledOpt ? ' is-disabled' : ''}`}
                      onMouseEnter={() => setHighlightIndex(idx)}
                      onClick={() => !disabledOpt && handleSelect(opt)}
                      data-option-index={idx}
                      aria-selected={toStr(opt.OptionValue) === selectedValue}
                      role='option'
                    >
                      <div className='searchSelectInput__option-text'>
                        {toStr(opt.OptionName)}
                      </div>
                      {/* <div className='searchSelectInput__option-sub'>
                        {toStr(opt.OptionValue) !== toStr(opt.OptionName)
                          ? toStr(opt.OptionValue)
                          : ''}
                      </div> */}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SearchSelectInput;
